/**
 * Trailblazers treasure rewards.
 *
 * An admin authors a pool of reward items for a game — credits, augments, Cartel
 * Market items, or free-form custom prizes — and the pool is then randomly
 * distributed across the galaxy map's nodes. The first team to clear a node wins
 * whatever treasure sits on it.
 *
 * Each item carries a DISTRIBUTION tier (low / mid / high) which biases *where*
 * it tends to land: a "high" item gravitates toward the last rounds of the map, a
 * "low" one toward the first. The bias is a gaussian over the node's derived
 * round depth, never a guarantee — see ../lifecycle/trlTreasureDistribution.
 *
 * Naming note: `TRL_TREASURE_FOUND` / `TrlNodeCapturedBroadcast` are about a team
 * CAPTURING a node and predate this feature. "Treasure" as a noun here means an
 * authored reward item; the two are awarded together but modelled separately.
 *
 * Second naming note: that distribution tier is NOT the SWTOR item quality that
 * tints a Cartel reward's name — that is `CartelItemQuality`, stamped onto an
 * award from the platform item. The two are unrelated and must not be conflated.
 *
 * Third naming note: "cartel" rewards are SWTOR items from the SWTORBIT platform's
 * item catalogue (`itemId`), usually Cartel Market items. The kind keeps its
 * original name, and older entries may still carry a `cartelItemId` from games'
 * retired hand-managed catalogue (PLATFORM_INTEGRATION.md, Phase 2).
 */
import { toCartelItemQuality } from './global-data';
// ============================================================================
// Constants
// ============================================================================
/** Distribution tiers, ordered lowest → highest. */
export const TRL_TREASURE_TIERS = ['low', 'mid', 'high'];
/** Human labels for the tiers (admin UI). */
export const TRL_TREASURE_TIER_LABELS = {
    low: 'Low',
    mid: 'Middle',
    high: 'High',
};
/** The reward kinds an admin can author. */
export const TRL_TREASURE_KINDS = ['credits', 'augment', 'cartel', 'custom'];
export const TRL_TREASURE_KIND_LABELS = {
    credits: 'Credits',
    augment: 'Augment',
    cartel: 'Cartel item',
    custom: 'Custom',
};
/** Hard cap on the pool size, so one game can't author an unbounded list. */
export const TRL_TREASURE_MAX_ITEMS = 200;
/** Hard cap on copies of a single item. */
export const TRL_TREASURE_MAX_QUANTITY = 100;
/** Hard cap on the consolation pool size. Smaller than the main pool: it is a short list. */
export const TRL_CONSOLATION_MAX_ITEMS = 50;
/**
 * Default drop chance per kind, in percent.
 *
 * Currency always pays out something — a runner-up who "won" 0 credits reads as a bug — while
 * a discrete item is a coin flip, so a haul of armour and dyes stays a surprise.
 */
export const TRL_CONSOLATION_DEFAULT_CHANCE = {
    credits: 100,
    augment: 100,
    cartel: 50,
    custom: 50,
};
/**
 * Bounds on a tier's gaussian spread (sigma), measured in rounds.
 *
 * At the minimum the curve is a near-spike on the tier's target round; at the
 * maximum it is flat enough to be effectively uniform across any realistic map
 * depth, which is how an admin opts out of the bias.
 */
export const TRL_TREASURE_MIN_SPREAD = 0.25;
export const TRL_TREASURE_MAX_SPREAD = 10;
/** Off, with nothing authored — so an existing game's behaviour is unchanged. */
export const DEFAULT_TRL_CONSOLATION_CONFIG = {
    enabled: false,
    items: [],
};
/** Empty pool with a moderate spread on every tier, and consolation switched off. */
export const DEFAULT_TRL_TREASURE_CONFIG = {
    items: [],
    tierSpread: { low: 1, mid: 1, high: 1 },
    consolation: DEFAULT_TRL_CONSOLATION_CONFIG,
};
/**
 * A treasure list is "unset" when it has no items — nothing to distribute.
 *
 * Deliberately ignores the consolation pool: consolation only ever fires on a planet holding
 * placed treasure, so a game with nothing but consolation entries really does have nothing.
 */
export function isTrlTreasureConfigEmpty(config) {
    return !config || config.items.length === 0;
}
/**
 * The effective consolation pool for a game — authored config, else the built-in default.
 *
 * Mirrors `resolveTrlSabotageConfig`. Every read site goes through this, so an older config
 * row that predates the feature reads as "off" instead of throwing on a missing key.
 */
export function resolveTrlConsolationConfig(config) {
    return config?.consolation ?? DEFAULT_TRL_CONSOLATION_CONFIG;
}
// ============================================================================
// Display
// ============================================================================
/**
 * One-line label for a reward, used in the admin lists, the map builder and the
 * player notification. Kept here so every surface words a reward identically.
 */
export function describeTrlTreasureItem(item) {
    switch (item.kind) {
        case 'credits':
            return `${item.amount.toLocaleString('en-US')} credits`;
        case 'augment':
            return `${item.amount.toLocaleString('en-US')} augment${item.amount === 1 ? '' : 's'}`;
        case 'cartel':
            return item.cartelItemName ?? 'Cartel item';
        case 'custom':
            return item.title;
    }
}
/**
 * Rewards that have no uploaded image but do have bundled client artwork.
 *
 * The id is NAMED here rather than imported because this package is loaded by
 * the Node API server, which can carry no image imports — the id is resolved to
 * a real url by `TRL_TREASURE_ICONS` in `@swtorbit/shared-assets`, whose
 * `Record` fails to compile the moment an id is added without artwork.
 */
export const TRL_TREASURE_ICON_ASSETS = ['credits', 'augment'];
/**
 * Glyphs. `TRL_TREASURE_FALLBACK_GLYPH` covers a row whose icon cannot be drawn
 * at all; the other two are the fallback for their reward's bundled artwork
 * failing to load, and the wording used anywhere a row is rendered as text.
 */
export const TRL_TREASURE_FALLBACK_GLYPH = '🎁';
const CREDITS_GLYPH = '💰';
const AUGMENT_GLYPH = '⚙️';
/**
 * Collapse an awarded item list into display rows.
 *
 * The list handed to this function is one entry per PLACEMENT — a node holding
 * two copies of the same reward yields that reward twice, and `item.quantity` on
 * those entries is the pool-wide copy count across the whole map, which is
 * meaningless here. So copies are counted by occurrence and `quantity` is
 * ignored entirely.
 *
 * Credits and augments collapse to a single row each whose `quantity` is the
 * summed payout (a team is paid one number, not four); cartel rewards group by
 * item id and custom ones by title. First-appearance order is preserved so
 * a staggered reveal follows the order the admin authored.
 */
export function groupTrlTreasureItems(items) {
    const rows = new Map();
    /**
     * Merge `row` into its group, adding `add` to the running quantity. The group
     * keeps the BEST tier it has seen, so one high-tier copy lifts the whole row.
     *
     * Every other field is taken from the first copy seen and never revisited, so
     * `quality` follows `name` and `icon`: if two awards of one catalogue id were
     * stamped either side of an admin edit and disagree, first appearance wins.
     */
    const upsert = (add, row) => {
        const existing = rows.get(row.key);
        if (!existing) {
            rows.set(row.key, { ...row, quantity: add });
            return;
        }
        existing.quantity += add;
        if (TRL_TREASURE_TIERS.indexOf(row.tier) > TRL_TREASURE_TIERS.indexOf(existing.tier)) {
            existing.tier = row.tier;
        }
    };
    for (const item of items) {
        switch (item.kind) {
            case 'credits':
                upsert(item.amount, {
                    key: 'credits',
                    name: 'Credits',
                    type: 'Currency',
                    quantity: 0,
                    tier: item.tier,
                    icon: { kind: 'asset', asset: 'credits', glyph: CREDITS_GLYPH },
                });
                break;
            case 'augment':
                upsert(item.amount, {
                    key: 'augment',
                    name: 'Augments',
                    type: 'Currency',
                    quantity: 0,
                    tier: item.tier,
                    icon: { kind: 'asset', asset: 'augment', glyph: AUGMENT_GLYPH },
                });
                break;
            case 'cartel':
                upsert(1, {
                    key: cartelRowKey(item),
                    name: item.cartelItemName ?? 'Cartel item',
                    type: 'Cartel Market',
                    quantity: 0,
                    tier: item.tier,
                    // Awards are read back out of JSONB with a bare cast, so an absent,
                    // stale or garbage value arrives here unvalidated. This is the one
                    // place it is narrowed: anything unrecognised becomes `undefined`,
                    // which renders exactly as it did before quality existed.
                    quality: toCartelItemQuality(item.cartelQuality),
                    icon: { kind: 'cartel', url: item.cartelIconUrl },
                });
                break;
            case 'custom':
                upsert(1, {
                    key: `custom:${item.title}`,
                    name: item.title,
                    type: item.description ?? 'Reward',
                    quantity: 0,
                    tier: item.tier,
                    icon: { kind: 'map-icon', id: item.icon, color: item.iconColor },
                });
                break;
        }
    }
    // A single augment reads better in the singular; everything else is fine as-is.
    const augments = rows.get('augment');
    if (augments && augments.quantity === 1)
        augments.name = 'Augment';
    return [...rows.values()];
}
/** Grouping key for a cartel reward: its platform item, else its legacy catalogue id. */
function cartelRowKey(item) {
    return item.itemId ? `cartel:${item.itemId}` : `cartel-legacy:${item.cartelItemId ?? ''}`;
}
/**
 * True for a cartel reward that has no platform item yet: authored against games'
 * retired `cartel_items` catalogue and not converted. The admin editor flags these
 * for relinking; an award of one renders by name only.
 */
export function isTrlTreasureItemUnlinked(item) {
    return item.kind === 'cartel' && !item.itemId;
}
/**
 * Every distinct platform item id referenced by a treasure pool, in the order
 * the admin authored them. Unlinked (legacy) entries have none and are skipped.
 *
 * Used by the icon prewarm (see {@link TrlTreasureIconsRequest}) to ask the
 * server which items this game will need pictures of. Deliberately returns ids
 * rather than resolving anything: the platform is called server-side only.
 *
 * Covers the consolation pool too. A consolation cartel reward is revealed on exactly the
 * same render path as a placed one, so leaving it out would show a runner-up a blank box.
 */
export function collectTrlTreasureItemIds(config) {
    if (!config)
        return [];
    const ids = new Set();
    for (const item of config.items) {
        if (item.kind === 'cartel' && item.itemId)
            ids.add(item.itemId);
    }
    for (const item of resolveTrlConsolationConfig(config).items) {
        if (item.kind === 'cartel' && item.itemId)
            ids.add(item.itemId);
    }
    return [...ids];
}
/**
 * The overall quality of the treasure sitting on one node, as the average of its
 * items' tiers (low 0 / mid 1 / high 2, rounded). Returns `undefined` when the
 * node carries no treasure, which callers render as "no badge at all".
 *
 * This derived grade is the ONLY treasure information a player receives about an
 * unclaimed node: it lets a team weigh one route against another without ever
 * disclosing the item ids or the rewards themselves. See `stripAdminMapFields`,
 * which computes it while stripping the admin-only `treasureItemIds`.
 */
export function deriveTrlTreasureGrade(items) {
    if (items.length === 0)
        return undefined;
    const total = items.reduce((sum, item) => sum + TRL_TREASURE_TIERS.indexOf(item.tier), 0);
    const index = Math.round(total / items.length);
    return TRL_TREASURE_TIERS[Math.min(TRL_TREASURE_TIERS.length - 1, Math.max(0, index))];
}
// ============================================================================
// Normalisation
// ============================================================================
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
function clampSpread(raw) {
    const n = Number(raw);
    if (!Number.isFinite(n))
        return 1;
    return Math.min(TRL_TREASURE_MAX_SPREAD, Math.max(TRL_TREASURE_MIN_SPREAD, n));
}
function normalizeTier(raw) {
    return TRL_TREASURE_TIERS.includes(raw) ? raw : 'mid';
}
function normalizeQuantity(raw) {
    const n = Number(raw);
    if (!Number.isFinite(n))
        return 1;
    return Math.min(TRL_TREASURE_MAX_QUANTITY, Math.max(1, Math.round(n)));
}
/** Non-negative integer amount, for credits/augments. */
function normalizeAmount(raw) {
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0)
        return 0;
    return Math.round(n);
}
function normalizeText(raw, max) {
    return typeof raw === 'string' ? raw.trim().slice(0, max) : '';
}
/** A platform item id: a positive integer, carried as a string. */
const PLATFORM_ITEM_ID_RE = /^[1-9]\d{0,11}$/;
/**
 * The identifying and display fields of a cartel reward, shared by the main pool
 * and the consolation pool. Null when it has neither a platform `itemId` nor a
 * legacy `cartelItemId`, i.e. nothing to identify it by.
 */
function normalizeCartelIdentity(it) {
    const rawItemId = typeof it.itemId === 'number' ? String(it.itemId) : normalizeText(it.itemId, 16);
    const itemId = PLATFORM_ITEM_ID_RE.test(rawItemId) ? rawItemId : '';
    const cartelItemId = normalizeText(it.cartelItemId, 64);
    if (!itemId && !cartelItemId)
        return null;
    const cartelItemName = normalizeText(it.cartelItemName, 200);
    const cartelIconUrl = normalizeText(it.cartelIconUrl, 512);
    const cartelQuality = toCartelItemQuality(it.cartelQuality);
    return {
        ...(itemId ? { itemId } : {}),
        ...(cartelItemId ? { cartelItemId } : {}),
        ...(cartelItemName ? { cartelItemName } : {}),
        ...(cartelIconUrl ? { cartelIconUrl } : {}),
        ...(cartelQuality ? { cartelQuality } : {}),
    };
}
/**
 * Sanitise one untrusted item. Returns null when the entry is unusable (no id,
 * unknown kind, or a kind-specific requirement missing), so the caller can drop
 * it rather than persist something the UI can't render.
 */
function normalizeItem(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const it = raw;
    const id = normalizeText(it.id, 64);
    if (!id)
        return null;
    const base = { id, tier: normalizeTier(it.tier), quantity: normalizeQuantity(it.quantity) };
    switch (it.kind) {
        case 'credits':
            return { ...base, kind: 'credits', amount: normalizeAmount(it.amount) };
        case 'augment':
            return { ...base, kind: 'augment', amount: normalizeAmount(it.amount) };
        case 'cartel': {
            const identity = normalizeCartelIdentity(it);
            return identity ? { ...base, kind: 'cartel', ...identity } : null;
        }
        case 'custom': {
            const title = normalizeText(it.title, 120);
            if (!title)
                return null;
            const description = normalizeText(it.description, 500);
            const icon = normalizeText(it.icon, 120);
            const iconColorRaw = normalizeText(it.iconColor, 32);
            const iconColor = HEX_COLOR_RE.test(iconColorRaw) ? iconColorRaw.toLowerCase() : '';
            return {
                ...base,
                kind: 'custom',
                title,
                ...(description ? { description } : {}),
                ...(icon ? { icon } : {}),
                ...(iconColor ? { iconColor } : {}),
            };
        }
        default:
            return null;
    }
}
/** Percentage in 0–100, rounded. Anything unparseable reads as "never". */
function normalizeChance(raw) {
    const n = Number(raw);
    if (!Number.isFinite(n))
        return 0;
    return Math.min(100, Math.max(0, Math.round(n)));
}
/**
 * Sanitise one untrusted consolation entry, or null when it is unusable.
 *
 * The identity fields are validated exactly as the main pool's are — same helpers, same
 * required-field rules — so a cartel entry with no item id at all is dropped either way. What
 * differs is the payload: a chance plus an inclusive range, with the bounds swapped if the
 * admin entered them backwards rather than rejecting the row over it.
 */
function normalizeConsolationItem(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const it = raw;
    const id = normalizeText(it.id, 64);
    if (!id)
        return null;
    const a = normalizeAmount(it.min);
    const b = normalizeAmount(it.max);
    const base = { id, chance: normalizeChance(it.chance), min: Math.min(a, b), max: Math.max(a, b) };
    switch (it.kind) {
        case 'credits':
            return { ...base, kind: 'credits' };
        case 'augment':
            return { ...base, kind: 'augment' };
        case 'cartel': {
            const identity = normalizeCartelIdentity(it);
            return identity ? { ...base, kind: 'cartel', ...identity } : null;
        }
        case 'custom': {
            const title = normalizeText(it.title, 120);
            if (!title)
                return null;
            const description = normalizeText(it.description, 500);
            const icon = normalizeText(it.icon, 120);
            const iconColorRaw = normalizeText(it.iconColor, 32);
            const iconColor = HEX_COLOR_RE.test(iconColorRaw) ? iconColorRaw.toLowerCase() : '';
            return {
                ...base,
                kind: 'custom',
                title,
                ...(description ? { description } : {}),
                ...(icon ? { icon } : {}),
                ...(iconColor ? { iconColor } : {}),
            };
        }
        default:
            return null;
    }
}
/** Sanitise the consolation block. A missing block reads as the default (off, empty). */
function normalizeConsolationConfig(raw) {
    if (!raw || typeof raw !== 'object')
        return { ...DEFAULT_TRL_CONSOLATION_CONFIG };
    const block = raw;
    const enabled = Boolean(block.enabled);
    if (!Array.isArray(block.items))
        return { enabled, items: [] };
    const seen = new Set();
    const items = [];
    for (const entry of block.items) {
        if (items.length >= TRL_CONSOLATION_MAX_ITEMS)
            break;
        const item = normalizeConsolationItem(entry);
        if (!item || seen.has(item.id))
            continue;
        seen.add(item.id);
        items.push(item);
    }
    return { enabled, items };
}
/**
 * Sanitise untrusted admin input into a valid {@link TrlTreasureConfig}: drop
 * unusable items, de-duplicate ids (first wins), clamp quantities and spreads,
 * and cap the pool size. Mirrors `normalizeTrlRadarConfig`.
 *
 * An empty pool is a legitimate result (the admin cleared the list), so unlike
 * the radar config this never falls back to defaults for the items.
 *
 * This is the single choke point for BOTH the admin save and a bundle import — the import
 * writes through the same `TRL_UPDATE_TREASURE_CONFIG` handler — so the consolation block
 * needs no validation of its own anywhere else.
 */
export function normalizeTrlTreasureConfig(raw) {
    const spreadRaw = raw?.tierSpread;
    const tierSpread = {
        low: clampSpread(spreadRaw?.low),
        mid: clampSpread(spreadRaw?.mid),
        high: clampSpread(spreadRaw?.high),
    };
    const consolation = normalizeConsolationConfig(raw?.consolation);
    const rawItems = raw?.items;
    if (!Array.isArray(rawItems))
        return { items: [], tierSpread, consolation };
    const seen = new Set();
    const items = [];
    for (const entry of rawItems) {
        if (items.length >= TRL_TREASURE_MAX_ITEMS)
            break;
        const item = normalizeItem(entry);
        if (!item || seen.has(item.id))
            continue;
        seen.add(item.id);
        items.push(item);
    }
    return { items, tierSpread, consolation };
}
/**
 * Drop assignment ids that don't exist in the pool. Called on save so a node can
 * never reference a reward the admin has since deleted.
 */
export function pruneTrlTreasureAssignments(assignedIds, config) {
    if (!assignedIds || assignedIds.length === 0)
        return [];
    const known = new Set((config?.items ?? []).map((i) => i.id));
    return assignedIds.filter((id) => known.has(id));
}
/** Structural equality — used to skip no-op saves. */
export function trlTreasureConfigEquals(a, b) {
    if (!a || !b)
        return a == null && b == null;
    return JSON.stringify(normalizeTrlTreasureConfig(a)) === JSON.stringify(normalizeTrlTreasureConfig(b));
}
